import api from './api';

export interface EmployeeAttendanceFormData {
  userId: string;
  status: string;
  informationType?: string;
  remarks?: string;
}

export interface BulkEmployeeAttendanceData {
  schoolId: string;
  date: string;
  attendanceRecords?: EmployeeAttendanceFormData[];
  // Employees the caller wants to explicitly UN-mark for this date. The
  // backend soft-deletes (isActive=false) any existing record for each id.
  removeUserIds?: string[];
  applyToAll?: boolean;
  status?: string;
  informationType?: string;
  remarks?: string;
}

// New interface for attendance record with punch details
export interface AttendanceRecord {
  status: string;
  informationType?: string;
  remarks?: string;
  firstPunch?: string;
  lastPunch?: string;
  totalWorkMinutes?: number;
  lateByMinutes?: number;
  punchCount?: number;
  isManualOverride?: boolean;
}

// Work settings interface
export interface WorkSettings {
  workStartTime: string;
  workEndTime: string;
  lateThresholdMinutes: number;
  halfDayThresholdMinutes: number;
  minWorkMinutesForFullDay: number;
  weeklyOffs?: number[];
}

export class EmployeeAttendanceService {
  // Format date to YYYY-MM-DD for API
  static formatDateForAPI(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Format minutes to hours:minutes display
  static formatMinutesToDuration(minutes: number | undefined): string {
    if (minutes === undefined || minutes === null) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }

  // Format punch time for display (UTC to local)
  static formatPunchTime(punchTime: string | undefined): string {
    if (!punchTime) return '-';
    const date = new Date(punchTime);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  }

  // Get attendance for a specific school and date
  async getAttendanceBySchoolAndDate(schoolId: string, date: string) {
    const response = await api.get('/employee-attendance', {
      params: { schoolId, date }
    });
    return response.data.data;
  }

  // Mark bulk attendance for employees
  async markBulkAttendance(data: BulkEmployeeAttendanceData) {
    const response = await api.post('/employee-attendance/bulk', data);
    return response.data.data;
  }

  // Get monthly attendance for employees
  async getMonthlyAttendance(
    schoolId: string, 
    year: number, 
    month: number,
    page?: number,
    limit?: number,
    search?: string
  ) {
    const params: any = { schoolId, year, month };
    if (page) params.page = page;
    if (limit) params.limit = limit;
    if (search) params.search = search;
    
    const response = await api.get('/employee-attendance/monthly', { params });
    return response.data.data;
  }

  // Mark all employees as present
  async markAllPresent(schoolId: string, date: string) {
    const response = await api.post('/employee-attendance/mark-all-present', {
      schoolId,
      date
    });
    return response.data.data;
  }

  // Get attendance statistics
  async getAttendanceStatistics(schoolId: string, startDate: string, endDate: string) {
    const response = await api.get('/employee-attendance/statistics', {
      params: { schoolId, startDate, endDate }
    });
    return response.data.data;
  }

  // Get work settings for school
  async getWorkSettings(schoolId: string): Promise<WorkSettings> {
    const response = await api.get(`/work-settings/${schoolId}`);
    return response.data.data;
  }

  // Update work settings for school
  async updateWorkSettings(schoolId: string, settings: Partial<WorkSettings>) {
    const response = await api.put(`/work-settings/${schoolId}`, settings);
    return response.data.data;
  }

  /** Every employee's month for the register PDF — pages through the grid 100 at a time. */
  async getMonthlyAttendanceAll(schoolId: string, year: number, month: number) {
    const first = await this.getMonthlyAttendance(schoolId, year, month, 1, 100);
    const rows = [...first.employeeAttendanceData];
    for (let page = 2; page <= first.pagination.totalPages; page += 1) {
      // eslint-disable-next-line no-await-in-loop -- pages are sequential by nature
      const next = await this.getMonthlyAttendance(schoolId, year, month, page, 100);
      rows.push(...next.employeeAttendanceData);
    }
    return { ...first, employeeAttendanceData: rows };
  }
}

const employeeAttendanceService = new EmployeeAttendanceService();
export default employeeAttendanceService;
